'use client';

import React, { useState, useCallback, useEffect } from 'react';
import Link from 'next/link';
import { useForm, useFieldArray } from 'react-hook-form';
import { toast } from 'sonner';
import {
  Plus, Trash2, ChevronDown, ChevronUp, Save, AlertCircle,
  RotateCcw, ArrowLeft, Download, Upload, Eye, Bitcoin, Settings2, Zap
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useCurve } from '@/context/CurveContext';
import { MaturityData, MaturityKey, InstantMarket, calcInstantAPR, DEFAULT_INSTANT_MARKETS, WeightingMethod } from '../data/initialData';

interface FormValues {
  apr: string;
  totalLiquidity: string;
  totalBorrowDemand: string;
  supplyOrders: { rate: string; size: string; id: string }[];
  borrowOrders: { rate: string; size: string; id: string }[];
}

const parseNum = (v: string) => parseFloat(v.replace(/,/g, '')) || 0;
let idCounter = 2000;
const genId = () => `new_${idCounter++}`;

const formatUSD = (v: number) => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v}`;
};

// ── Edit Header ──────────────────────────────────────────────────────────────

function EditHeader({ onReset }: { onReset: () => void }) {
  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2.5">
              <AppLogo size={32} />
              <span className="font-bold text-lg tracking-tight text-gray-900 hidden sm:block">
                Spine Finance
              </span>
            </div>
            <div className="hidden md:block w-px h-6 bg-gray-200" />
            <div className="hidden md:flex items-center gap-2">
              <div className="bg-[#EEE9FF] rounded-md p-1.5">
                <Settings2 size={13} className="text-[#6C47FF]" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-900 leading-tight">Curve Configuration</span>
                <span className="text-xs text-gray-400 leading-tight">Edit market parameters</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onReset}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-red-600 bg-white border border-gray-200 rounded-lg px-3 py-2 hover:border-red-200 hover:bg-red-50 transition-all duration-150 active:scale-95"
            >
              <RotateCcw size={14} />
              <span className="hidden sm:inline font-medium">Reset</span>
            </button>
            <Link
              href="/yield-curve-simulator"
              className="flex items-center gap-2 text-sm font-semibold text-white bg-[#6C47FF] border border-[#6C47FF] rounded-lg px-3 py-2 hover:bg-[#5A38E8] transition-all duration-150 active:scale-95"
            >
              <Eye size={14} />
              <span className="hidden sm:inline">View Data Page</span>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

// ── Instant Market Inputs Section ────────────────────────────────────────────

interface InstantInputsProps {
  data: MaturityData;
  onChange: (updated: MaturityData) => void;
  weighting: WeightingMethod;
  onWeightingChange: (m: WeightingMethod) => void;
}

const PROTOCOL_COLORS: Record<string, string> = {
  Aave: '#B6509E',
  Morpho: '#2470FF',
  Euler: '#E8612C',
};

function InstantInputs({ data, onChange, weighting, onWeightingChange }: InstantInputsProps) {
  const [markets, setMarkets] = useState<InstantMarket[]>(
    data.instantMarkets && data.instantMarkets.length > 0
      ? data.instantMarkets
      : DEFAULT_INSTANT_MARKETS
  );
  const [isDirty, setIsDirty] = useState(false);

  // Sync from context whenever the saved data changes externally (reset, import, or after save)
  // Only overwrite local state when not dirty so in-progress edits are preserved
  const contextMarketsKey = JSON.stringify(data.instantMarkets);
  useEffect(() => {
    if (!isDirty) {
      if (data.instantMarkets && data.instantMarkets.length > 0) {
        setMarkets(data.instantMarkets);
      } else {
        setMarkets(DEFAULT_INSTANT_MARKETS);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMarketsKey]);

  const derivedAPR = calcInstantAPR(markets, weighting);
  const totalSupply = markets.reduce((s, m) => s + m.totalSupply, 0);
  const totalBorrowed = markets.reduce((s, m) => s + m.totalBorrowed, 0);

  const updateField = (index: number, field: keyof InstantMarket, value: string | number) => {
    setMarkets((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: typeof value === 'string' ? (field === 'protocol' ? value : parseFloat(value) || 0) : value };
      return next;
    });
    setIsDirty(true);
  };

  const handleSave = () => {
    const updated: MaturityData = {
      ...data,
      instantMarkets: markets,
      apr: derivedAPR,
      totalLiquidity: totalSupply,
      totalBorrowDemand: totalBorrowed,
    };
    onChange(updated);
    setIsDirty(false);
    toast.success('Instant updated', {
      description: `Weighted APR: ${derivedAPR.toFixed(2)}% · Supply: ${formatUSD(totalSupply)}`,
      duration: 2500,
    });
  };

  return (
    <div className="spine-card overflow-hidden ring-1 ring-[#6C47FF]/20">
      {/* Section header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-[#F8F7FC] to-white">
        <div className="bg-[#EEE9FF] rounded-lg p-2">
          <Zap size={16} className="text-[#6C47FF]" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Instant Maturity</p>
          <h3 className="text-base font-semibold text-gray-900">Instant Market Inputs</h3>
        </div>
        <div className="hidden sm:flex flex-col items-end">
          <span className="text-xs text-gray-400 font-medium">Derived APR</span>
          <span className="text-lg font-mono font-bold text-[#6C47FF]">{derivedAPR.toFixed(2)}%</span>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Info note */}
        <div className="flex items-start gap-2.5 bg-[#EEE9FF]/60 border border-[#6C47FF]/15 rounded-xl px-4 py-3">
          <Zap size={14} className="text-[#6C47FF] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[#4F46E5] font-medium leading-relaxed">
            Enter benchmark market data for each onchain lending protocol. The Instant APR is automatically derived as a weighted average of protocol rates.
          </p>
        </div>

        {/* Weighting toggle */}
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-gray-500">APR Weighting:</span>
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5 gap-0.5">
            <button
              type="button"
              onClick={() => onWeightingChange('totalBorrowed')}
              className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-all ${
                weighting === 'totalBorrowed' ?'bg-white text-[#6C47FF] shadow-sm' :'text-gray-500 hover:text-gray-700'
              }`}
            >
              By Borrowed
            </button>
            <button
              type="button"
              onClick={() => onWeightingChange('totalSupply')}
              className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-all ${
                weighting === 'totalSupply' ?'bg-white text-[#6C47FF] shadow-sm' :'text-gray-500 hover:text-gray-700'
              }`}
            >
              By Supply
            </button>
          </div>
          <span className="text-xs text-gray-400">
            {weighting === 'totalBorrowed' ? 'Weighted by total borrowed (default)' : 'Weighted by total supply'}
          </span>
        </div>

        {/* Protocol rows */}
        <div>
          <div className="grid grid-cols-[140px_1fr_1fr_1fr] gap-3 px-1 mb-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Protocol</span>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">Rate (%)</span>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">Total Supply ($)</span>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">Total Borrowed ($)</span>
          </div>

          <div className="space-y-2">
            {markets.map((market, index) => {
              const color = PROTOCOL_COLORS[market.protocol] ?? '#6C47FF';
              const util = market.totalSupply > 0
                ? ((market.totalBorrowed / market.totalSupply) * 100).toFixed(1)
                : '0.0';
              return (
                <div key={index} className="bg-white border border-gray-100 rounded-xl p-3 hover:border-gray-200 transition-colors shadow-sm">
                  <div className="grid grid-cols-[140px_1fr_1fr_1fr] gap-3 items-center">
                    {/* Protocol name */}
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                      <input
                        type="text"
                        value={market.protocol}
                        onChange={(e) => updateField(index, 'protocol', e.target.value)}
                        className="spine-input text-xs py-1.5 font-semibold"
                      />
                    </div>
                    {/* Rate */}
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        value={market.rate}
                        onChange={(e) => updateField(index, 'rate', e.target.value)}
                        className="spine-input text-xs py-1.5 pr-6 text-right font-mono"
                        placeholder="4.20"
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                    </div>
                    {/* Total Supply */}
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">$</span>
                      <input
                        type="number"
                        step="1000000"
                        value={market.totalSupply}
                        onChange={(e) => updateField(index, 'totalSupply', e.target.value)}
                        className="spine-input text-xs py-1.5 pl-5 text-right font-mono"
                        placeholder="120000000"
                      />
                    </div>
                    {/* Total Borrowed */}
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">$</span>
                      <input
                        type="number"
                        step="1000000"
                        value={market.totalBorrowed}
                        onChange={(e) => updateField(index, 'totalBorrowed', e.target.value)}
                        className="spine-input text-xs py-1.5 pl-5 text-right font-mono"
                        placeholder="84000000"
                      />
                    </div>
                  </div>
                  {/* Utilization mini-bar */}
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-16 flex-shrink-0">Util: {util}%</span>
                    <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${parseFloat(util) > 85 ? 'bg-amber-400' : 'bg-[#6C47FF]/50'}`}
                        style={{ width: `${Math.min(100, parseFloat(util))}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Derived summary */}
        <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Derived Summary</p>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Weighted APR</p>
              <p className="text-base font-mono font-bold text-[#6C47FF]">{derivedAPR.toFixed(2)}%</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Total Supply</p>
              <p className="text-base font-mono font-bold text-green-700">{formatUSD(totalSupply)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Total Borrowed</p>
              <p className="text-base font-mono font-bold text-orange-600">{formatUSD(totalBorrowed)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Utilization</p>
              <p className="text-base font-mono font-bold text-gray-700">
                {totalSupply > 0 ? ((totalBorrowed / totalSupply) * 100).toFixed(1) : '0.0'}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Save bar */}
      <div className="bg-gray-50/80 border-t border-gray-100 px-5 py-3">
        <button
          type="button"
          onClick={handleSave}
          className="spine-btn-primary w-full flex items-center justify-center gap-2"
        >
          <Save size={14} />
          Save Instant Changes
        </button>
      </div>
    </div>
  );
}

// ── Maturity Form ────────────────────────────────────────────────────────────

interface MaturityFormProps {
  maturityData: MaturityData;
  isExpanded: boolean;
  onToggle: () => void;
  onSave: (data: MaturityData) => void;
}

function MaturityForm({ maturityData, isExpanded, onToggle, onSave }: MaturityFormProps) {
  const [supplyExpanded, setSupplyExpanded] = useState(true);
  const [borrowExpanded, setBorrowExpanded] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const { register, control, handleSubmit, reset, watch, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      apr: maturityData.apr.toString(),
      totalLiquidity: maturityData.totalLiquidity.toString(),
      totalBorrowDemand: maturityData.totalBorrowDemand.toString(),
      supplyOrders: maturityData.supplyOrders.map((o) => ({ rate: o.rate.toString(), size: o.size.toString(), id: o.id })),
      borrowOrders: maturityData.borrowOrders.map((o) => ({ rate: o.rate.toString(), size: o.size.toString(), id: o.id })),
    },
  });

  const { fields: supplyFields, append: appendSupply, remove: removeSupply } = useFieldArray({ control, name: 'supplyOrders' });
  const { fields: borrowFields, append: appendBorrow, remove: removeBorrow } = useFieldArray({ control, name: 'borrowOrders' });

  useEffect(() => {
    reset({
      apr: maturityData.apr.toString(),
      totalLiquidity: maturityData.totalLiquidity.toString(),
      totalBorrowDemand: maturityData.totalBorrowDemand.toString(),
      supplyOrders: maturityData.supplyOrders.map((o) => ({ rate: o.rate.toString(), size: o.size.toString(), id: o.id })),
      borrowOrders: maturityData.borrowOrders.map((o) => ({ rate: o.rate.toString(), size: o.size.toString(), id: o.id })),
    });
    setIsDirty(false);
  }, [maturityData, reset]);

  const watchAll = watch();
  useEffect(() => { setIsDirty(true); }, [JSON.stringify(watchAll)]);

  const onSubmit = useCallback((values: FormValues) => {
    const updated: MaturityData = {
      maturity: maturityData.maturity,
      apr: parseNum(values.apr),
      totalLiquidity: parseNum(values.totalLiquidity),
      totalBorrowDemand: parseNum(values.totalBorrowDemand),
      supplyOrders: values.supplyOrders.map((o) => ({ id: o.id, rate: parseNum(o.rate), size: parseNum(o.size) })),
      borrowOrders: values.borrowOrders.map((o) => ({ id: o.id, rate: parseNum(o.rate), size: parseNum(o.size) })),
    };
    onSave(updated);
    setIsDirty(false);
    toast.success(`${maturityData.maturity} updated`, {
      description: `APR: ${updated.apr.toFixed(2)}% · Supply: ${formatUSD(updated.totalLiquidity)}`,
      duration: 2500,
    });
  }, [maturityData.maturity, onSave]);

  const utilization = maturityData.totalLiquidity > 0
    ? (maturityData.totalBorrowDemand / maturityData.totalLiquidity) * 100
    : 0;

  return (
    <div className={`spine-card overflow-hidden transition-all duration-200 ${isExpanded ? 'ring-1 ring-[#6C47FF]/20' : ''}`}>
      {/* Maturity header — always visible */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm transition-colors ${
            isExpanded ? 'bg-[#6C47FF] text-white' : 'bg-[#EEE9FF] text-[#6C47FF]'
          }`}>
            {maturityData.maturity}
          </div>
          <div className="text-left">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-gray-900">{maturityData.maturity} Maturity</span>
              {isDirty && isExpanded && (
                <span className="text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                  Unsaved
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-0.5">
              <span className="text-xs font-mono font-bold text-[#6C47FF]">{maturityData.apr.toFixed(2)}% APR</span>
              <span className="text-xs text-gray-400">·</span>
              <span className="text-xs text-green-700 font-mono">{formatUSD(maturityData.totalLiquidity)} supply</span>
              <span className="text-xs text-gray-400">·</span>
              <span className="text-xs text-orange-600 font-mono">{formatUSD(maturityData.totalBorrowDemand)} demand</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Utilization bar */}
          <div className="hidden sm:flex flex-col items-end gap-1">
            <span className="text-xs text-gray-400 font-mono">{utilization.toFixed(0)}% util</span>
            <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${utilization > 85 ? 'bg-amber-400' : 'bg-[#6C47FF]/60'}`}
                style={{ width: `${Math.min(100, utilization)}%` }}
              />
            </div>
          </div>
          {isExpanded ? (
            <ChevronUp size={16} className="text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
          )}
        </div>
      </button>

      {/* Expanded form */}
      {isExpanded && (
        <form onSubmit={handleSubmit(onSubmit)} className="border-t border-gray-100">
          <div className="p-5 space-y-5">
            {/* Core Metrics */}
            <div>
              <p className="section-label mb-3">Core Metrics</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* APR */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Fixed APR <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      {...register('apr', {
                        required: 'Required',
                        validate: (v) => { const n = parseNum(v); return (n > 0 && n <= 50) || 'Invalid APR'; },
                      })}
                      className="spine-input pr-8"
                      placeholder="6.00"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-gray-400">%</span>
                  </div>
                  {errors.apr && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle size={11} /> {errors.apr.message}</p>}
                </div>

                {/* Total Supply */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Total Supply <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-mono text-gray-400">$</span>
                    <input
                      {...register('totalLiquidity', { required: 'Required', validate: (v) => parseNum(v) > 0 || 'Must be positive' })}
                      className="spine-input pl-6"
                      placeholder="12000000"
                    />
                  </div>
                  {errors.totalLiquidity && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle size={11} /> {errors.totalLiquidity.message}</p>}
                </div>

                {/* Total Borrow Demand */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Borrow Demand <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-mono text-gray-400">$</span>
                    <input
                      {...register('totalBorrowDemand', { required: 'Required', validate: (v) => parseNum(v) > 0 || 'Must be positive' })}
                      className="spine-input pl-6"
                      placeholder="9700000"
                    />
                  </div>
                  {errors.totalBorrowDemand && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle size={11} /> {errors.totalBorrowDemand.message}</p>}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100" />

            {/* Supply Orders */}
            <div>
              <button
                type="button"
                onClick={() => setSupplyExpanded((v) => !v)}
                className="w-full flex items-center justify-between group mb-3"
              >
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-sm bg-[#6C47FF] inline-block opacity-70" />
                  <p className="section-label text-[#6C47FF]">Lender Supply Orders</p>
                  <span className="text-xs bg-[#EEE9FF] text-[#6C47FF] font-semibold rounded-full px-2 py-0.5">
                    {supplyFields.length} levels
                  </span>
                </div>
                {supplyExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
              </button>

              {supplyExpanded && (
                <div className="space-y-2">
                  <div className="grid grid-cols-[1fr_1fr_auto] gap-2 px-1">
                    <span className="text-xs text-gray-400 font-semibold">Rate (%)</span>
                    <span className="text-xs text-gray-400 font-semibold">Size ($)</span>
                    <span className="w-7" />
                  </div>
                  {supplyFields.length === 0 && (
                    <div className="text-center py-4 text-xs text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                      No supply orders — add a level below
                    </div>
                  )}
                  {supplyFields.map((field, index) => (
                    <div key={field.id} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                      <input
                        {...register(`supplyOrders.${index}.rate`, { required: true, validate: (v) => parseNum(v) > 0 })}
                        className="spine-input text-xs py-1.5 text-[#6C47FF]"
                        placeholder="5.50"
                      />
                      <input
                        {...register(`supplyOrders.${index}.size`, { required: true, validate: (v) => parseNum(v) > 0 })}
                        className="spine-input text-xs py-1.5"
                        placeholder="2000000"
                      />
                      <button
                        type="button"
                        onClick={() => removeSupply(index)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => appendSupply({ rate: '', size: '', id: genId() })}
                    className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-[#6C47FF] bg-[#EEE9FF] border border-dashed border-[#6C47FF]/30 rounded-lg py-2 hover:bg-[#E0D9FF] transition-colors active:scale-95"
                  >
                    <Plus size={12} />
                    Add Supply Level
                  </button>
                </div>
              )}
            </div>

            <div className="border-t border-gray-100" />

            {/* Borrow Orders */}
            <div>
              <button
                type="button"
                onClick={() => setBorrowExpanded((v) => !v)}
                className="w-full flex items-center justify-between group mb-3"
              >
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-sm bg-orange-500 inline-block opacity-70" />
                  <p className="section-label text-orange-700">Borrower Demand Orders</p>
                  <span className="text-xs bg-orange-100 text-orange-700 font-semibold rounded-full px-2 py-0.5">
                    {borrowFields.length} levels
                  </span>
                </div>
                {borrowExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
              </button>

              {borrowExpanded && (
                <div className="space-y-2">
                  <div className="grid grid-cols-[1fr_1fr_auto] gap-2 px-1">
                    <span className="text-xs text-gray-400 font-semibold">Rate (%)</span>
                    <span className="text-xs text-gray-400 font-semibold">Size ($)</span>
                    <span className="w-7" />
                  </div>
                  {borrowFields.length === 0 && (
                    <div className="text-center py-4 text-xs text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                      No borrow orders — add a level below
                    </div>
                  )}
                  {borrowFields.map((field, index) => (
                    <div key={field.id} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                      <input
                        {...register(`borrowOrders.${index}.rate`, { required: true, validate: (v) => parseNum(v) > 0 })}
                        className="spine-input text-xs py-1.5 text-orange-700"
                        placeholder="6.00"
                      />
                      <input
                        {...register(`borrowOrders.${index}.size`, { required: true, validate: (v) => parseNum(v) > 0 })}
                        className="spine-input text-xs py-1.5"
                        placeholder="1000000"
                      />
                      <button
                        type="button"
                        onClick={() => removeBorrow(index)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => appendBorrow({ rate: '', size: '', id: genId() })}
                    className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-orange-700 bg-orange-50 border border-dashed border-orange-200 rounded-lg py-2 hover:bg-orange-100 transition-colors active:scale-95"
                  >
                    <Plus size={12} />
                    Add Borrow Level
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Save bar */}
          <div className="bg-gray-50/80 border-t border-gray-100 px-5 py-3">
            {isDirty && (
              <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mb-2 border border-amber-100">
                <AlertCircle size={12} />
                <span className="font-medium">Unsaved changes — save to update the Data Page</span>
              </div>
            )}
            <button
              type="submit"
              className="spine-btn-primary w-full flex items-center justify-center gap-2"
            >
              <Save size={14} />
              Save {maturityData.maturity} Changes
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ── JSON Import/Export Panel ─────────────────────────────────────────────────

function JsonPanel({ exportJSON, importJSON }: { exportJSON: () => string; importJSON: (s: string) => boolean }) {
  const [jsonText, setJsonText] = useState('');
  const [showImport, setShowImport] = useState(false);

  const handleExport = () => {
    const json = exportJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'spine-curve-config.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Configuration exported');
  };

  const handleImport = () => {
    if (!jsonText.trim()) { toast.error('Paste JSON first'); return; }
    const ok = importJSON(jsonText);
    if (ok) {
      toast.success('Configuration imported successfully');
      setJsonText('');
      setShowImport(false);
    } else {
      toast.error('Invalid JSON format');
    }
  };

  return (
    <div className="spine-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <p className="section-label">JSON Import / Export</p>
      </div>
      <div className="flex gap-2 mb-3">
        <button
          onClick={handleExport}
          className="flex items-center gap-2 text-sm font-semibold text-[#6C47FF] bg-[#EEE9FF] rounded-lg px-4 py-2 hover:bg-[#E0D9FF] transition-colors active:scale-95"
        >
          <Download size={14} />
          Export JSON
        </button>
        <button
          onClick={() => setShowImport((v) => !v)}
          className="flex items-center gap-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg px-4 py-2 hover:border-[#6C47FF]/30 hover:bg-[#EEE9FF] hover:text-[#6C47FF] transition-colors active:scale-95"
        >
          <Upload size={14} />
          Import JSON
        </button>
      </div>
      {showImport && (
        <div className="space-y-2">
          <textarea
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            className="spine-input w-full h-32 text-xs font-mono resize-none"
            placeholder="Paste JSON configuration here..."
          />
          <button
            onClick={handleImport}
            className="spine-btn-primary flex items-center gap-2"
          >
            <Upload size={13} />
            Apply Import
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main Edit Page ───────────────────────────────────────────────────────────

export default function EditPageClient() {
  const { maturities, updateMaturity, resetData, exportJSON, importJSON, instantWeighting, setInstantWeighting } = useCurve();
  const [expandedMaturity, setExpandedMaturity] = useState<MaturityKey | null>('4W');

  const instantData = maturities.find((m) => m.maturity === 'Instant')!;

  const handleReset = useCallback(() => {
    resetData();
    toast.info('All data reset to initial values');
  }, [resetData]);

  const handleToggle = (maturity: MaturityKey) => {
    setExpandedMaturity((prev) => (prev === maturity ? null : maturity));
  };

  // Fixed maturities only (exclude Instant — handled separately above)
  const fixedMaturities = maturities.filter((m) => m.maturity !== 'Instant');

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8F7FC] to-[#F3F1FA]">
      <EditHeader onReset={handleReset} />

      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">

        {/* Page title */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 pt-2 pb-1">
          <div>
            <p className="section-label mb-1.5">Configuration Panel</p>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Edit Curve Parameters</h1>
            <p className="text-sm text-gray-500 mt-1">
              Changes save instantly to the Data Page via shared state
            </p>
          </div>
          <Link
            href="/yield-curve-simulator"
            className="flex items-center gap-2 text-sm font-semibold text-[#6C47FF] bg-[#EEE9FF] rounded-lg px-4 py-2 hover:bg-[#E0D9FF] transition-colors active:scale-95 self-start sm:self-auto"
          >
            <ArrowLeft size={14} />
            Back to Data Page
          </Link>
        </div>

        {/* Info banner */}
        <div className="flex items-start gap-3 bg-[#EEE9FF] border border-[#6C47FF]/20 rounded-xl px-4 py-3">
          <Bitcoin size={16} className="text-[#6C47FF] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4F46E5] font-medium">
            Instant maturity is built from protocol-level benchmark data (Aave, Morpho, Euler). Fixed maturities (1W–24W) use creditbook order ladders.
            All changes update the yield curve on the Data Page instantly.
          </p>
        </div>

        {/* ── Instant Inputs (top, always visible) ── */}
        <InstantInputs
          data={instantData}
          onChange={updateMaturity}
          weighting={instantWeighting}
          onWeightingChange={setInstantWeighting}
        />

        {/* ── Fixed Maturity forms ── */}
        <div className="space-y-3">
          {fixedMaturities.map((m) => (
            <MaturityForm
              key={m.maturity}
              maturityData={m}
              isExpanded={expandedMaturity === m.maturity}
              onToggle={() => handleToggle(m.maturity)}
              onSave={updateMaturity}
            />
          ))}
        </div>

        {/* JSON panel */}
        <JsonPanel exportJSON={exportJSON} importJSON={importJSON} />

        {/* Footer */}
        <div className="flex items-center justify-center pb-6">
          <p className="text-xs text-gray-400 text-center max-w-lg">
            <span className="text-[#6C47FF] font-medium">Spine Finance</span> — Bitcoin-Backed Fixed-Rate Credit Infrastructure
            <br />
            Configuration is persisted in local storage and shared with the Data Page.
          </p>
        </div>
      </main>
    </div>
  );
}
